import React, { Suspense, lazy, useContext, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { authActions } from "./app/store";
import { SuperOrgContext } from "./contexts/SuperOrgContextProvider";
import { getOrganisations } from "./features/org/orgSlice";
import { history } from "./Helpers/history";
import { hasRole, ROLE_NAMES } from "./Helpers/roles";
import PrivateRoute from "./ui/components/PrivateRoute";
import AuthenticatedAppShell from "./ui/components/AuthenticatedAppShell";
import RouteMetadata from "./ui/components/RouteMetadata";
import Button from "./ui/components/Button";
import Dialog from "./ui/components/Dialog";
import ActiveElrs from "./ui/pages/ActiveElrs";
import DataExports from "./ui/pages/DataExports";
import FourZeroFourPage from "./ui/pages/FourZeroFourPage";
import Login from "./ui/pages/Login";
import Loading from "./ui/pages/Loading";
import Organisations from "./ui/pages/Organisations";
import ProjectsLibrary from "./ui/pages/ProjectsLibrary";
import PacksRegister from "./ui/pages/PacksRegister";
import TempPacks from "./ui/pages/TempPacks";
import CreatePack from "./ui/pages/PackCreation/CreatePack";
import ViewPack from "./ui/pages/ViewPack";
import ReassignPack from "./ui/pages/ReassignPack";

const WsmDashboard = lazy(() => import("./ui/pages/WsmDashboard"));
const People = lazy(() => import("./ui/pages/People"));

const packManagerRoles = [ROLE_NAMES.SUPER_ADMIN, ROLE_NAMES.PLANNER];
const superAdminRoles = [ROLE_NAMES.SUPER_ADMIN];

const restricted = (element, allowedRoles) => (
  <PrivateRoute allowedRoles={allowedRoles}>{element}</PrivateRoute>
);

export default function App() {
  const authUser = useSelector((state) => state.auth.user);
  const authHydrated = useSelector((state) => state.auth.hydrated);
  const authHydrating = useSelector((state) => state.auth.isHydrating);
  const organisations = useSelector((state) => state.organisations);
  const isSuperAdmin = hasRole(authUser, ROLE_NAMES.SUPER_ADMIN);
  const dispatch = useDispatch();
  const { super_organisation_id } = useContext(SuperOrgContext);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  history.navigate = useNavigate();
  history.location = useLocation();
  history.toast = toast;

  useEffect(() => {
    if (!authHydrated && !authHydrating) dispatch(authActions.hydrateCurrentUser());
  }, [authHydrated, authHydrating, dispatch]);

  useEffect(() => {
    if (isSuperAdmin && !organisations?.payload?.data) dispatch(getOrganisations());
  }, [dispatch, isSuperAdmin, organisations?.payload?.data]);

  const pageContent = (
    <Suspense fallback={<Loading />}>
      <Routes>
        {(!authHydrated || authHydrating) && <Route path="*" element={<Loading />} />}
        {authHydrated && !authHydrating && (
          <>
            <Route element={<PrivateRoute><AuthenticatedAppShell /></PrivateRoute>}>
              <Route path="/" element={<WsmDashboard />} />
              <Route path="/people" element={restricted(<People />, superAdminRoles)} />
              <Route path="/organisations" element={restricted(<Organisations />, superAdminRoles)} />
              <Route path="/packs/resume" element={restricted(<TempPacks />, packManagerRoles)} />
              <Route path="/packs/create/:id" element={restricted(<CreatePack />, packManagerRoles)} />
              <Route path="/packs/create" element={restricted(<CreatePack />, packManagerRoles)} />
              <Route path="/packs/register" element={<PacksRegister />} />
              <Route path="/packs/exports" element={restricted(<DataExports />, packManagerRoles)} />
              <Route path="/library/elrs" element={<ActiveElrs />} />
              <Route path="/library/swp-projects" element={<ProjectsLibrary />} />
              <Route path="/library/projects" element={<Navigate replace to="/library/swp-projects" />} />
              <Route path="/library" element={<Navigate replace to="/library/elrs" />} />
              <Route path="/projects" element={<Navigate replace to="/library/swp-projects" />} />
              <Route path="/view/:id" element={<ViewPack />} />
              <Route path="/view/:id/reassign" element={restricted(<ReassignPack />, packManagerRoles)} />
              <Route path="*" element={<FourZeroFourPage />} />
            </Route>
            <Route path="/login" element={<Login />} />
         </>
        )}
      </Routes>
    </Suspense>
  );

  return (
    <div className="core">
      <RouteMetadata />
      <div className="wrap">
        <div className="main--single-full-width" key={super_organisation_id}>
          {pageContent}
          <ToastContainer />
        </div>
      </div>

      <Dialog
        actions={<Button onClick={() => setDisclaimerAccepted(true)}>Proceed</Button>}
        closeOnBackdrop={false}
        description="Please confirm before continuing."
        onClose={() => undefined}
        open={Boolean(authUser) && !disclaimerAccepted}
        size="small"
        title="Test environment"
      >
        <p>Please be aware this is a test environment and functionality is being updated continuously.</p>
      </Dialog>
    </div>
  );
}
